import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Clock, Calendar, Globe, CheckCircle2, 
  AlertCircle, LogOut, ChevronLeft, ChevronRight,
  TrendingUp, FileText, ArrowRight, ShieldCheck
} from 'lucide-react';
import { 
  verifyEmployee, 
  getAuthenticatedEmployee, 
  setAuthenticatedEmployee, 
  clearAuthenticatedEmployee,
  getEmployees,
  saveReport,
  getReportsForEmployee,
  formatDate,
  getWeekRange,
  COMMON_TIMEZONES
} from '../../lib/reportService';

const SkillReport = () => {
  const navigate = useNavigate();

  // Auth State
  const [currentUser, setCurrentUser] = useState(null);
  const [loginForm, setLoginForm] = useState({ id: '', pin: '' });
  const [loginError, setLoginError] = useState('');

  // Active View Tab
  const [activeTab, setActiveTab] = useState('daily'); // 'daily' | 'weekly' | 'monthly'

  // Form State for Daily Submission
  const detectedTz = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata';
    } catch {
      return 'Asia/Kolkata';
    }
  }, []);

  const [reportDate, setReportDate] = useState(formatDate(new Date()));
  const [hoursWorked, setHoursWorked] = useState('6');
  const [timezone, setTimezone] = useState(detectedTz);
  const [tasksDone, setTasksDone] = useState('');
  const [notes, setNotes] = useState('');
  const [formFeedback, setFormFeedback] = useState({ type: '', message: '' });

  // Weekly View State
  const [weekOffset, setWeekOffset] = useState(0); // 0 = current week, -1 = last week, etc.

  // Employee's own reports list
  const [myReports, setMyReports] = useState([]);

  // Check login on load
  useEffect(() => {
    const existing = getAuthenticatedEmployee();
    if (existing) {
      setCurrentUser(existing);
    }
  }, []);

  // Reload employee reports when user is active
  useEffect(() => {
    if (currentUser) {
      loadMyReports();
    }
  }, [currentUser]);

  // If a report already exists for selected reportDate, auto-fill it
  useEffect(() => {
    if (!currentUser) return;
    const existing = myReports.find(r => r.date === reportDate);
    if (existing) {
      setHoursWorked(String(existing.hoursWorked));
      setTimezone(existing.timezone || detectedTz);
      setTasksDone(existing.tasksDone || '');
      setNotes(existing.notes || '');
    } else {
      setHoursWorked('6');
      setTasksDone('');
      setNotes('');
    }
  }, [reportDate, myReports, currentUser, detectedTz]);

  const loadMyReports = () => {
    if (!currentUser) return;
    const reports = getReportsForEmployee(currentUser.id);
    setMyReports(reports);
  };

  // Handle Login
  const handleLogin = (e) => {
    e.preventDefault();
    setLoginError('');
    const emp = verifyEmployee(loginForm.id, loginForm.pin);
    if (emp) {
      setAuthenticatedEmployee(emp);
      setCurrentUser(emp);
      setLoginError('');
    } else {
      setLoginError('Invalid Employee ID or PIN. Default test PIN is 1234.');
    }
  };

  const handleLogout = () => {
    clearAuthenticatedEmployee();
    setCurrentUser(null);
  };

  // Handle Report Submission
  const handleSubmitReport = (e) => {
    e.preventDefault();
    if (!tasksDone.trim()) {
      setFormFeedback({ type: 'error', message: 'Please describe the tasks completed today.' });
      return;
    }

    const hours = parseFloat(hoursWorked);
    if (isNaN(hours) || hours <= 0 || hours > 24) {
      setFormFeedback({ type: 'error', message: 'Please enter a valid hours worked value (between 0.5 and 24).' });
      return;
    }

    try {
      saveReport({
        employeeId: currentUser.id,
        employeeName: currentUser.name,
        role: currentUser.role,
        date: reportDate,
        timezone,
        hoursWorked: hours,
        tasksDone: tasksDone.trim(),
        notes: notes.trim()
      });

      setFormFeedback({ type: 'success', message: 'Daily report saved successfully!' });
      loadMyReports();
      setTimeout(() => setFormFeedback({ type: '', message: '' }), 4000);
    } catch {
      setFormFeedback({ type: 'error', message: 'Failed to save report. Please try again.' });
    }
  };

  // Current Target Week Range
  const selectedWeekRange = useMemo(() => {
    const target = new Date();
    target.setDate(target.getDate() + (weekOffset * 7));
    return getWeekRange(target);
  }, [weekOffset]);

  // Reports in the selected week for current user
  const weekReports = useMemo(() => {
    return myReports.filter(r => selectedWeekRange.days.includes(r.date));
  }, [myReports, selectedWeekRange]);

  const weeklyTotalHours = useMemo(() => {
    return weekReports.reduce((sum, r) => sum + (parseFloat(r.hoursWorked) || 0), 0);
  }, [weekReports]);

  // Current Month Reports
  const currentMonthReports = useMemo(() => {
    const now = new Date();
    const currentYearMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    return myReports.filter(r => r.date.startsWith(currentYearMonth));
  }, [myReports]);

  const monthlyTotalHours = useMemo(() => {
    return currentMonthReports.reduce((sum, r) => sum + (parseFloat(r.hoursWorked) || 0), 0);
  }, [currentMonthReports]);

  // -------------------------------------------------------------
  // LOGIN SCREEN (if employee is not signed in)
  // -------------------------------------------------------------
  if (!currentUser) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col justify-center items-center px-4 relative overflow-hidden">
        {/* Ambient glow backgrounds */}
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute inset-0 bg-grid-pattern opacity-10 pointer-events-none"></div>

        <div className="w-full max-w-md bg-gray-900/70 backdrop-blur-md p-8 rounded-2xl border border-orange-500/30 relative z-10 shadow-2xl">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-orange-500/10 border border-orange-500/30 mb-4 text-orange-500">
              <Clock size={28} />
            </div>
            <h1 className="text-2xl font-bold futuristic-text text-white tracking-wide">InSkill Report</h1>
            <p className="text-gray-400 text-sm mt-1">Intern & Employee Daily Work Reporting</p>
          </div>

          {loginError && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-sm mb-6 flex items-start space-x-2">
              <AlertCircle size={18} className="mt-0.5 flex-shrink-0" />
              <span>{loginError}</span>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label className="block text-gray-300 text-xs font-semibold uppercase tracking-wider mb-2">
                Employee ID
              </label>
              <input
                type="text"
                value={loginForm.id}
                onChange={(e) => setLoginForm({ ...loginForm, id: e.target.value.toUpperCase() })}
                placeholder="e.g. INSK-001"
                required
                className="w-full bg-black/60 border border-orange-500/30 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none transition-colors uppercase font-mono tracking-wider"
              />
            </div>

            <div>
              <label className="block text-gray-300 text-xs font-semibold uppercase tracking-wider mb-2">
                Security PIN / Password
              </label>
              <input
                type="password"
                value={loginForm.pin}
                onChange={(e) => setLoginForm({ ...loginForm, pin: e.target.value })}
                placeholder="Enter 4-digit PIN"
                required
                className="w-full bg-black/60 border border-orange-500/30 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-orange-500 focus:outline-none transition-colors"
              />
            </div>

            <button
              type="submit"
              className="w-full neon-button-primary py-3 rounded-lg font-bold flex items-center justify-center space-x-2 cursor-pointer transition-all"
            >
              <span>Access Reporting Portal</span>
              <ArrowRight size={18} />
            </button>
          </form>

          <div className="mt-8 text-center pt-6 border-t border-gray-800">
            <button
              onClick={() => navigate('/admin')}
              className="text-xs text-gray-400 hover:text-orange-500 transition-colors inline-flex items-center space-x-1"
            >
              <ShieldCheck size={14} />
              <span>Admin Login Portal</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------
  // AUTHENTICATED EMPLOYEE REPORTING PORTAL
  // -------------------------------------------------------------
  return (
    <div className="min-h-screen bg-black text-white relative">
      {/* Background Grid Pattern */}
      <div className="absolute inset-0 bg-grid-pattern opacity-5 pointer-events-none"></div>

      {/* Top Navigation Bar */}
      <header className="bg-gray-900/70 border-b border-orange-500/20 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <div 
              onClick={() => navigate('/')} 
              className="cursor-pointer flex items-center space-x-2"
            >
              <h1 className="text-xl font-bold futuristic-text text-white tracking-wide">InSkill</h1>
              <span className="text-xs font-mono uppercase bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded border border-orange-500/30">
                Reporting
              </span>
            </div>
          </div>

          {/* User Profile & Logout */}
          <div className="flex items-center space-x-4">
            <div className="hidden sm:flex flex-col text-right">
              <span className="text-sm font-semibold text-white">{currentUser.name}</span>
              <span className="text-xs text-orange-400 font-mono">{currentUser.id} • {currentUser.role}</span>
            </div>
            <div className="w-9 h-9 rounded-full bg-orange-500/20 border border-orange-500/40 flex items-center justify-center text-orange-400 font-bold text-sm">
              {currentUser.name.charAt(0)}
            </div>
            <button
              onClick={handleLogout}
              className="text-gray-400 hover:text-red-400 p-2 rounded-lg hover:bg-gray-800 transition-colors flex items-center space-x-1 text-xs"
              title="Logout"
            >
              <LogOut size={16} />
              <span className="hidden md:inline">Sign Out</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
        {/* Navigation Tabs */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8 pb-4 border-b border-gray-800">
          <div className="flex space-x-2">
            {[
              { id: 'daily', label: 'Submit Daily Report', icon: FileText },
              { id: 'weekly', label: 'My Weekly Report', icon: Calendar },
              { id: 'monthly', label: 'Monthly Summary', icon: TrendingUp }
            ].map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-4 py-2.5 rounded-xl font-medium text-sm transition-all duration-200 ${
                    isActive
                      ? 'bg-orange-500 text-black font-bold shadow-lg shadow-orange-500/20'
                      : 'bg-gray-900/60 text-gray-400 hover:text-white hover:bg-gray-800 border border-gray-800'
                  }`}
                >
                  <Icon size={16} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* Timezone Badge */}
          <div className="flex items-center space-x-2 text-xs text-gray-400 bg-gray-900/80 px-3 py-1.5 rounded-lg border border-gray-800">
            <Globe size={14} className="text-orange-500" />
            <span>Time Zone: <strong className="text-gray-200">{timezone}</strong></span>
          </div>
        </div>

        {/* ------------------------------------------------------------- */}
        {/* TAB 1: DAILY REPORT SUBMISSION FORM                           */}
        {/* ------------------------------------------------------------- */}
        {activeTab === 'daily' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Form Column */}
            <div className="lg:col-span-2">
              <div className="bg-gray-900/50 backdrop-blur-sm border border-orange-500/20 rounded-2xl p-6 sm:p-8">
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-800">
                  <div>
                    <h2 className="text-xl font-bold futuristic-text text-white">Daily Work Log</h2>
                    <p className="text-xs text-gray-400 mt-0.5">
                      Log your daily hours and summarize the tasks you accomplished.
                    </p>
                  </div>
                  <span className="text-xs font-mono text-orange-400 bg-orange-500/10 px-2.5 py-1 rounded border border-orange-500/20">
                    {reportDate === formatDate(new Date()) ? "Today's Entry" : reportDate}
                  </span>
                </div>

                {formFeedback.message && (
                  <div className={`p-4 rounded-xl mb-6 flex items-start space-x-3 ${
                    formFeedback.type === 'success' 
                      ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-400' 
                      : 'bg-red-500/10 border border-red-500/30 text-red-400'
                  }`}>
                    {formFeedback.type === 'success' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
                    <span className="text-sm font-medium">{formFeedback.message}</span>
                  </div>
                )}

                <form onSubmit={handleSubmitReport} className="space-y-6">
                  {/* Date & Hours Row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-gray-300 text-xs font-semibold uppercase tracking-wider mb-2">
                        Reporting Date
                      </label>
                      <input
                        type="date"
                        value={reportDate}
                        max={formatDate(new Date())}
                        onChange={(e) => setReportDate(e.target.value)}
                        required
                        className="w-full bg-black/60 border border-orange-500/30 rounded-lg px-4 py-2.5 text-white focus:border-orange-500 focus:outline-none transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-gray-300 text-xs font-semibold uppercase tracking-wider mb-2">
                        Working Hours
                      </label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          step="0.5"
                          min="0.5"
                          max="24"
                          value={hoursWorked}
                          onChange={(e) => setHoursWorked(e.target.value)}
                          required
                          className="w-full bg-black/60 border border-orange-500/30 rounded-lg px-4 py-2.5 text-white font-mono text-lg focus:border-orange-500 focus:outline-none transition-colors"
                        />
                        <div className="flex space-x-1">
                          {['3', '5', '6', '9'].map(h => (
                            <button
                              key={h}
                              type="button"
                              onClick={() => setHoursWorked(h)}
                              className={`text-xs px-2.5 py-2.5 rounded font-mono border transition-colors ${
                                hoursWorked === h 
                                  ? 'bg-orange-500 text-black font-bold border-orange-500' 
                                  : 'bg-black/40 text-gray-400 border-gray-800 hover:border-orange-500/40'
                              }`}
                            >
                              {h}h
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Timezone Selector */}
                  <div>
                    <label className="block text-gray-300 text-xs font-semibold uppercase tracking-wider mb-2">
                      Time Zone
                    </label>
                    <select
                      value={timezone}
                      onChange={(e) => setTimezone(e.target.value)}
                      className="w-full bg-black/60 border border-orange-500/30 rounded-lg px-4 py-2.5 text-white focus:border-orange-500 focus:outline-none transition-colors text-sm"
                    >
                      {COMMON_TIMEZONES.map(tz => (
                        <option key={tz.value} value={tz.value} className="bg-gray-900 text-white">
                          {tz.label}
                        </option>
                      ))}
                      {!COMMON_TIMEZONES.some(t => t.value === timezone) && (
                        <option value={timezone} className="bg-gray-900 text-white">
                          {timezone} (Detected)
                        </option>
                      )}
                    </select>
                  </div>

                  {/* Tasks Done / Comments */}
                  <div>
                    <label className="block text-gray-300 text-xs font-semibold uppercase tracking-wider mb-2">
                      What did you accomplish today? <span className="text-orange-500">*</span>
                    </label>
                    <textarea
                      rows={5}
                      value={tasksDone}
                      onChange={(e) => setTasksDone(e.target.value)}
                      placeholder="• Built user reporting interface at /skill/report&#10;• Connected state handling with local storage&#10;• Tested cross-browser layout on mobile and desktop"
                      required
                      className="w-full bg-black/60 border border-orange-500/30 rounded-lg p-4 text-white placeholder-gray-600 focus:border-orange-500 focus:outline-none transition-colors text-sm leading-relaxed"
                    />
                  </div>

                  {/* Blockers or Next Day Plan */}
                  <div>
                    <label className="block text-gray-300 text-xs font-semibold uppercase tracking-wider mb-2">
                      Notes / Blockers / Next Day Goals (Optional)
                    </label>
                    <input
                      type="text"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="e.g. Need code review on PR #12; next step is optimizing bundle size"
                      className="w-full bg-black/60 border border-orange-500/30 rounded-lg px-4 py-2.5 text-white placeholder-gray-600 focus:border-orange-500 focus:outline-none transition-colors text-sm"
                    />
                  </div>

                  {/* Submit Button */}
                  <div className="pt-2">
                    <button
                      type="submit"
                      className="w-full sm:w-auto neon-button-primary px-8 py-3.5 rounded-xl font-bold flex items-center justify-center space-x-2 cursor-pointer transition-all"
                    >
                      <CheckCircle2 size={18} />
                      <span>Save & Submit Daily Report</span>
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* Sidebar Overview Column */}
            <div className="space-y-6">
              {/* Weekly Snapshot Card */}
              <div className="bg-gray-900/50 backdrop-blur-sm border border-orange-500/20 rounded-2xl p-6">
                <h3 className="text-base font-bold text-white mb-4 flex items-center space-x-2">
                  <Calendar size={18} className="text-orange-500" />
                  <span>This Week's Progress</span>
                </h3>

                <div className="mb-4">
                  <div className="flex justify-between items-baseline mb-1">
                    <span className="text-2xl font-bold font-mono text-orange-400">{weeklyTotalHours} hrs</span>
                    <span className="text-xs text-gray-400 font-mono">Target: 40h</span>
                  </div>
                  <div className="w-full bg-black/60 rounded-full h-2.5 overflow-hidden border border-gray-800">
                    <div 
                      className="bg-gradient-to-r from-orange-600 to-orange-400 h-full rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(100, (weeklyTotalHours / 40) * 100)}%` }}
                    ></div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 text-center pt-2">
                  <div className="bg-black/40 p-3 rounded-xl border border-gray-800/80">
                    <span className="block text-lg font-bold font-mono text-white">{weekReports.length}</span>
                    <span className="text-xs text-gray-400">Days Logged</span>
                  </div>
                  <div className="bg-black/40 p-3 rounded-xl border border-gray-800/80">
                    <span className="block text-lg font-bold font-mono text-white">
                      {weekReports.length > 0 ? (weeklyTotalHours / weekReports.length).toFixed(1) : '0'}h
                    </span>
                    <span className="text-xs text-gray-400">Avg / Day</span>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-gray-800">
                  <button
                    onClick={() => setActiveTab('weekly')}
                    className="w-full text-xs text-orange-400 hover:text-orange-300 font-semibold flex items-center justify-center space-x-1 py-1"
                  >
                    <span>View Full Weekly Breakdown</span>
                    <ChevronRight size={14} />
                  </button>
                </div>
              </div>

              {/* Policy & Privacy Reminder */}
              <div className="bg-gray-900/30 rounded-2xl p-6 border border-gray-800/80 text-xs text-gray-400 space-y-2">
                <div className="flex items-center space-x-2 text-white font-medium">
                  <ShieldCheck size={16} className="text-orange-500" />
                  <span>Privacy Notice</span>
                </div>
                <p className="leading-relaxed">
                  You are viewing your own personal logs. Only organization administrators have access to company-wide reports.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ------------------------------------------------------------- */}
        {/* TAB 2: WEEKLY BREAKDOWN FOR THE LOGGED-IN EMPLOYEE            */}
        {/* ------------------------------------------------------------- */}
        {activeTab === 'weekly' && (
          <div className="space-y-6">
            {/* Week Navigator Bar */}
            <div className="bg-gray-900/50 backdrop-blur-sm border border-orange-500/20 rounded-2xl p-4 sm:p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setWeekOffset(prev => prev - 1)}
                  className="p-2 rounded-lg bg-black/60 border border-gray-800 hover:border-orange-500/40 text-gray-300 hover:text-orange-400 transition-colors"
                >
                  <ChevronLeft size={20} />
                </button>
                <div className="text-center sm:text-left">
                  <span className="text-xs text-orange-400 font-mono uppercase tracking-wider block">
                    {weekOffset === 0 ? 'Current Week' : weekOffset === -1 ? 'Last Week' : `Week (${weekOffset > 0 ? '+' : ''}${weekOffset})`}
                  </span>
                  <h3 className="text-lg font-bold text-white">
                    {selectedWeekRange.mondayStr} to {selectedWeekRange.sundayStr}
                  </h3>
                </div>
                <button
                  onClick={() => setWeekOffset(prev => prev + 1)}
                  disabled={weekOffset >= 0}
                  className={`p-2 rounded-lg bg-black/60 border border-gray-800 text-gray-300 transition-colors ${
                    weekOffset >= 0 ? 'opacity-40 cursor-not-allowed' : 'hover:border-orange-500/40 hover:text-orange-400'
                  }`}
                >
                  <ChevronRight size={20} />
                </button>
              </div>

              {weekOffset !== 0 && (
                <button
                  onClick={() => setWeekOffset(0)}
                  className="text-xs text-orange-400 hover:underline font-mono"
                >
                  Jump to Current Week
                </button>
              )}

              {/* Total Hours Badge */}
              <div className="bg-black/60 px-5 py-2.5 rounded-xl border border-orange-500/30 flex items-center space-x-3">
                <Clock size={18} className="text-orange-500" />
                <div>
                  <span className="text-xs text-gray-400 block">Weekly Total</span>
                  <span className="text-lg font-mono font-bold text-white">{weeklyTotalHours} Hours</span>
                </div>
              </div>
            </div>

            {/* 7-Day Visual Strip */}
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
              {selectedWeekRange.days.map((dayStr) => {
                const dayDate = new Date(`${dayStr}T12:00:00`);
                const dayName = dayDate.toLocaleDateString('en-US', { weekday: 'short' });
                const reportForDay = myReports.find(r => r.date === dayStr);
                const isToday = dayStr === formatDate(new Date());

                return (
                  <div
                    key={dayStr}
                    onClick={() => {
                      setReportDate(dayStr);
                      setActiveTab('daily');
                    }}
                    className={`cursor-pointer p-4 rounded-xl border transition-all text-center ${
                      reportForDay
                        ? 'bg-orange-500/10 border-orange-500/40 hover:border-orange-500'
                        : isToday
                        ? 'bg-gray-900/60 border-orange-500/30 hover:border-orange-500/60'
                        : 'bg-black/40 border-gray-800/80 hover:border-gray-700'
                    }`}
                  >
                    <span className="text-xs font-semibold uppercase text-gray-400 block">{dayName}</span>
                    <span className="text-xs font-mono text-gray-500 block mb-2">{dayStr.slice(5)}</span>

                    {reportForDay ? (
                      <div className="space-y-1">
                        <span className="text-xl font-bold font-mono text-orange-400 block">
                          {reportForDay.hoursWorked}h
                        </span>
                        <span className="inline-block text-[10px] font-semibold uppercase tracking-wider text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded">
                          Logged
                        </span>
                      </div>
                    ) : (
                      <div className="space-y-1 py-1">
                        <span className="text-sm font-mono text-gray-600 block">-</span>
                        <span className="inline-block text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                          {isToday ? 'Pending' : 'Off'}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Daily Log Details Table / Cards */}
            <div className="bg-gray-900/50 backdrop-blur-sm border border-orange-500/20 rounded-2xl p-6">
              <h3 className="text-lg font-bold text-white mb-4 futuristic-text">Daily Logs for This Week</h3>

              {weekReports.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Clock size={36} className="mx-auto mb-3 opacity-30 text-orange-500" />
                  <p className="text-sm">No work logs submitted for this week yet.</p>
                  <button
                    onClick={() => setActiveTab('daily')}
                    className="mt-4 neon-button-primary px-4 py-2 rounded-lg text-xs font-bold"
                  >
                    Log Today's Work
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {weekReports.map(rep => (
                    <div 
                      key={rep.id} 
                      className="bg-black/50 border border-orange-500/15 rounded-xl p-5 hover:border-orange-500/30 transition-colors"
                    >
                      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2 mb-3 pb-3 border-b border-gray-800">
                        <div className="flex items-center space-x-3">
                          <span className="text-sm font-mono font-bold text-white bg-gray-800 px-2.5 py-1 rounded">
                            {rep.date}
                          </span>
                          <span className="text-xs text-orange-400 bg-orange-500/10 px-2 py-0.5 rounded border border-orange-500/20">
                            {rep.hoursWorked} Hours
                          </span>
                          <span className="text-xs text-gray-400 font-mono">
                            {rep.timezone || 'Asia/Kolkata'}
                          </span>
                        </div>
                        <button
                          onClick={() => {
                            setReportDate(rep.date);
                            setActiveTab('daily');
                          }}
                          className="text-xs text-gray-400 hover:text-orange-400 self-start sm:self-auto underline"
                        >
                          Edit Entry
                        </button>
                      </div>

                      <div className="text-sm text-gray-200 whitespace-pre-line leading-relaxed mb-2">
                        {rep.tasksDone}
                      </div>

                      {rep.notes && (
                        <div className="text-xs text-gray-400 italic bg-gray-900/40 p-2.5 rounded-lg border border-gray-800 mt-2">
                          <strong>Note:</strong> {rep.notes}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ------------------------------------------------------------- */}
        {/* TAB 3: MONTHLY SUMMARY                                        */}
        {/* ------------------------------------------------------------- */}
        {activeTab === 'monthly' && (
          <div className="space-y-6">
            {/* KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="bg-gray-900/50 backdrop-blur-sm border border-orange-500/20 rounded-2xl p-6">
                <span className="text-xs font-semibold uppercase text-gray-400 tracking-wider">
                  Total Monthly Hours
                </span>
                <div className="text-3xl font-bold font-mono text-orange-400 mt-2">
                  {monthlyTotalHours} hrs
                </div>
                <span className="text-xs text-gray-500 mt-1 block">
                  Current calendar month
                </span>
              </div>

              <div className="bg-gray-900/50 backdrop-blur-sm border border-orange-500/20 rounded-2xl p-6">
                <span className="text-xs font-semibold uppercase text-gray-400 tracking-wider">
                  Days Worked
                </span>
                <div className="text-3xl font-bold font-mono text-white mt-2">
                  {currentMonthReports.length} days
                </div>
                <span className="text-xs text-gray-500 mt-1 block">
                  Submissions recorded
                </span>
              </div>

              <div className="bg-gray-900/50 backdrop-blur-sm border border-orange-500/20 rounded-2xl p-6">
                <span className="text-xs font-semibold uppercase text-gray-400 tracking-wider">
                  Daily Average
                </span>
                <div className="text-3xl font-bold font-mono text-white mt-2">
                  {currentMonthReports.length > 0 ? (monthlyTotalHours / currentMonthReports.length).toFixed(1) : '0'} hrs
                </div>
                <span className="text-xs text-gray-500 mt-1 block">
                  Per logged working day
                </span>
              </div>
            </div>

            {/* Monthly History Table */}
            <div className="bg-gray-900/50 backdrop-blur-sm border border-orange-500/20 rounded-2xl p-6">
              <h3 className="text-lg font-bold text-white mb-4 futuristic-text">All Monthly Submissions</h3>

              {currentMonthReports.length === 0 ? (
                <p className="text-sm text-gray-500 py-6 text-center">No reports found for this month.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="border-b border-gray-800 text-xs uppercase text-gray-400">
                        <th className="py-3 px-4">Date</th>
                        <th className="py-3 px-4">Hours</th>
                        <th className="py-3 px-4">Time Zone</th>
                        <th className="py-3 px-4">Summary of Tasks Completed</th>
                        <th className="py-3 px-4 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800/60 font-sans">
                      {currentMonthReports.map(rep => (
                        <tr key={rep.id} className="hover:bg-black/30">
                          <td className="py-3.5 px-4 font-mono font-medium text-white">{rep.date}</td>
                          <td className="py-3.5 px-4 font-mono text-orange-400 font-bold">{rep.hoursWorked}h</td>
                          <td className="py-3.5 px-4 text-xs text-gray-400 font-mono">{rep.timezone}</td>
                          <td className="py-3.5 px-4 text-gray-300 max-w-md truncate">{rep.tasksDone}</td>
                          <td className="py-3.5 px-4 text-right">
                            <button
                              onClick={() => {
                                setReportDate(rep.date);
                                setActiveTab('daily');
                              }}
                              className="text-xs text-orange-400 hover:underline"
                            >
                              Edit
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default SkillReport;
